// Runtime-error vocabulary layer (audit/findings/frontend-ux-business-user-
// 2026-08.md §1.7): the same problem `configLabels.js` already solved for
// proposal review, applied to the error toasts a business user hits during
// ordinary daily work — a permission-denied on a data op, a config-save
// validator wall. Those errors currently arrive as raw config keys/internal
// concepts ("Operation 'update' not permitted on 'purchase_order'").
//
// This is a general mechanism, not a list of one-off string replacements:
// any single-quoted, snake_case identifier in a backend message gets
// humanize()'d, and a couple of the highest-traffic message SHAPES get a
// full friendly rewrite on top of that. A message this doesn't recognize at
// all still comes out at least as readable as it went in.
//
// Plain functions only, same Fast-Refresh reason as configLabels.js.

import { humanize, opLabel } from './configLabels';

const IDENTIFIER = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

// `'purchase_order'` -> `"Purchase order"`. Only touches tokens that look
// like a config key (lowercase snake_case) so an ordinary quoted word in an
// otherwise-human message is left alone.
function humanizeQuotedIdentifiers(message) {
  return message.replace(/'([a-z][a-z0-9_.]*)'/g, (full, ident) => (
    IDENTIFIER.test(ident) ? `"${humanize(ident)}"` : full
  ));
}

// Ordered by how often a business user actually hits them (routes_data.py's
// permission-denied is the daily one; the cross-app table-ownership message
// is the one that started this whole audit thread).
const TEMPLATES = [
  {
    re: /^Operation '(\w+)' not permitted on '([\w.]+)'$/,
    format: (m) => `You can't ${opLabel(m[1])} ${humanize(m[2]).toLowerCase()} records. Ask an admin for access.`,
  },
  // routes_data.py:906's preview_row shape - same meaning as the "Operation"
  // shape above, just without that leading word.
  {
    re: /^'(\w+)' not permitted on '([\w.]+)'$/,
    format: (m) => `You can't ${opLabel(m[1])} ${humanize(m[2]).toLowerCase()} records. Ask an admin for access.`,
  },
  // storage/gcs.py's file-field read-permission shape - no op to report, so
  // there's nothing to plug into opLabel().
  {
    re: /^Read not permitted on '([\w.]+)'$/,
    format: (m) => `You can't view ${humanize(m[1]).toLowerCase()} records. Ask an admin for access.`,
  },
  {
    re: /^Entity '([\w.]+)' claims table '([\w.]+)', which is owned by app '([^']+)'\. Import the entity from that app instead of redefining it\.$/,
    format: (m) => `"${humanize(m[1])}" is already set up in another app ("${m[3]}"). Import it from there instead of redefining it here.`,
  },
];

/** One error clause -> its friendliest available form. */
function translateOne(message) {
  const trimmed = message.trim();
  for (const { re, format } of TEMPLATES) {
    const m = trimmed.match(re);
    if (m) return format(m);
  }
  return humanizeQuotedIdentifiers(trimmed);
}

/**
 * Translate a raw backend error message for display. Handles the
 * "; "-joined wall a config-save validator failure returns (routes_apps.py's
 * `"; ".join(errors)`) by translating each clause on its own rather than
 * pattern-matching the whole wall at once.
 */
export function translateRuntimeError(message) {
  if (!message || typeof message !== 'string') return message;
  if (message.includes('; ')) {
    return message.split('; ').map(translateOne).join('; ');
  }
  return translateOne(message);
}
